import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Users, Activity, Zap, Database, Brain, Settings, Shield, Monitor, FileText, AlertCircle, Layers, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import SEO from '../components/SEO';
import AIReadinessAssessment from '../components/AIReadinessAssessment';

const CALENDLY_URL = "https://calendly.com/chengappa-nivadigital/30min";
const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://nivadigital.io';

const aiServicesSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      "@id": `${BASE_URL}/ai-services#service`,
      "name": "AI Services",
      "serviceType": "AI development, integration and managed AI services",
      "url": `${BASE_URL}/ai-services`,
      "description": "NIVA Digital builds AI from scratch for startups, integrates AI into legacy enterprise systems, and augments existing SaaS products with intelligent capabilities, delivered through a Build-Operate-Transfer model.",
      "provider": {
        "@type": "Organization",
        "name": "NIVA Digital",
        "url": BASE_URL,
        "logo": `${BASE_URL}/niva-logo.jpg`
      },
      "areaServed": ["United States", "India", "Worldwide"],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "AI Services",
        "itemListElement": [
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Greenfield AI for startups" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "AI integration into legacy enterprise stacks" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "SaaS product augmentation with AI features" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "AI strategy, data, models and autonomous workflows" } }
        ]
      }
    },
    {
      "@type": "Offer",
      "@id": `${BASE_URL}/ai-services#free-assessment`,
      "name": "Free AI Readiness Assessment",
      "price": "0",
      "priceCurrency": "USD",
      "url": `${BASE_URL}/ai-services`,
      "category": "AI Readiness Assessment",
      "description": "A free 5-question AI readiness assessment that gives you a personalised AI maturity score, a readiness level, and a recommended next step in under 2 minutes.",
      "availability": "https://schema.org/InStock",
      "seller": { "@type": "Organization", "name": "NIVA Digital", "url": BASE_URL }
    }
  ]
};

const AIServices = () => {
  const [showAssessment, setShowAssessment] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="AI Services & Free AI Readiness Assessment | NIVA Digital AI"
        description="NIVA Digital's AI services build AI from scratch for startups, integrate AI into legacy enterprise systems, and augment SaaS products. Take our free AI readiness assessment and get a personalised AI maturity score in under 2 minutes."
        keywords="AI services, free AI readiness assessment, AI maturity assessment, AI readiness test, AI pods, managed AI services, AI integration, enterprise AI, AI strategy, AI consulting"
        canonical={`${BASE_URL}/ai-services`}
        structuredData={aiServicesSchema}
      />

      {/* AI Readiness Assessment Modal */}
      <AIReadinessAssessment 
        isOpen={showAssessment} 
        onClose={() => setShowAssessment(false)} 
      />


      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-red-50 via-orange-50 to-amber-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: "url('https://images.pexels.com/photos/17483871/pexels-photo-17483871.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')" }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/75 via-white/55 to-white/75" aria-hidden="true" />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              From AI-Curious to <em className="not-italic text-red-600">AI-Capable.</em><br/>We will help you with this journey.
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              NIVA Digital builds AI from scratch for startups, integrates AI into legacy enterprise systems, and augments existing SaaS products, so every dollar of AI investment shows up in your revenue line.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
                  Start an AI Pilot <ArrowRight className="ml-2" size={20} />
                </Button>
              </a>
              <Link to="/delivery">
                <Button size="lg" variant="outline" className="px-8 py-6 text-lg border-gray-300 hover:border-red-600 hover:text-red-600" data-testid="see-operated-delivery-btn">
                  See How Operated Delivery Works
                </Button>
              </Link>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-gray-900">40<span className="text-red-600">,60%</span></div>
                <div className="text-sm text-gray-600 mt-1">Cost savings vs. US/UK AI teams</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-gray-900">4<span className="text-red-600">wk</span></div>
                <div className="text-sm text-gray-600 mt-1">Contract to first production AI</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-gray-900">BOT</div>
                <div className="text-sm text-gray-600 mt-1">Build · Operate · Transfer model</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why AI-First Section */}
      <section className="py-16 px-4 bg-gray-50 border-y border-gray-200">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col lg:flex-row gap-12 items-start">
            <div className="lg:w-1/4">
              <h2 className="text-2xl font-bold text-gray-900">
                Why <span className="text-red-600">AI-First</span><br/>matters now
              </h2>
            </div>
            <div className="lg:w-3/4 grid md:grid-cols-2 gap-6">
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">From data to revenue in weeks, not quarters.</h3>
                  <p className="text-sm text-gray-600">Pre-built AI patterns and reusable ML stacks compress delivery timelines dramatically.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">AI-native teams, not just outsourced engineers.</h3>
                  <p className="text-sm text-gray-600">Every engineer uses AI tooling as their default workflow, not an afterthought.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Layers className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Pod-based delivery for faster ROI.</h3>
                  <p className="text-sm text-gray-600">Small, focused AI squads move independently — fail fast, double down on what works.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Governed AI you can trust in production.</h3>
                  <p className="text-sm text-gray-600">Human-in-the-loop controls, audit trails, and compliance frameworks built into every deployment.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 px-4" id="our-services">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <p className="text-red-600 font-semibold text-sm tracking-wider uppercase mb-2">Our Services</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Three ways we deliver AI for your business</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Structured around your stage, your team, and how deeply you need AI embedded into your operations.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* AI Services Card */}
            <Card className="border-2 border-red-200 bg-red-50/30 hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6 text-red-600" />
                </div>
                <CardTitle className="text-xl">AI Services</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">Building AI from scratch for startups, integrating AI into legacy enterprise systems, and augmenting existing SaaS products with intelligent capabilities.</p>
                <ul className="space-y-2 text-sm text-gray-600 mb-6">
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" /> Greenfield AI for startups, zero to production</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" /> AI integration into legacy enterprise stacks</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" /> SaaS product augmentation with AI features</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" /> Strategy, data, models and autonomous workflows</li>
                </ul>
                <a href="#ai-detail" className="text-red-600 font-medium text-sm flex items-center gap-1 hover:gap-2 transition-all">
                  Explore AI Services <ArrowRight className="w-4 h-4" />
                </a>
              </CardContent>
            </Card>

            {/* AI-Enabled Pods Card */}
            <Card className="border-2 hover:border-gray-300 hover:shadow-lg transition-all">
              <CardHeader>
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-gray-700" />
                </div>
                <CardTitle className="text-xl">AI-Enabled Pods</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">Autonomous AI workforces that take over specific operational workflows, immediate, scalable AI capacity without hiring a single internal AI engineer.</p>
                <ul className="space-y-2 text-sm text-gray-600 mb-6">
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" /> Managed AI agents owning workflows end-to-end</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" /> Instant capacity, no recruiting, no lag</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" /> Scales with your operational demand</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" /> Outcome-linked scope with measurable KPIs</li>
                </ul>
                <a href="#pods" className="text-gray-700 font-medium text-sm flex items-center gap-1 hover:gap-2 transition-all">
                  See AI Pods <ArrowRight className="w-4 h-4" />
                </a>
              </CardContent>
            </Card>

            {/* Managed Services Card */}
            <Card className="border-2 hover:border-gray-300 hover:shadow-lg transition-all">
              <CardHeader>
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-4">
                  <Activity className="w-6 h-6 text-gray-700" />
                </div>
                <CardTitle className="text-xl">Managed Services</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">AI-enabled monitoring and reporting that keeps your systems healthy, your data clean, and your leadership team informed, without building an internal ops team.</p>
                <ul className="space-y-2 text-sm text-gray-600 mb-6">
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" /> AI-powered infrastructure monitoring</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" /> Automated reporting and anomaly detection</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" /> Model drift tracking and retraining pipelines</li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" /> Executive dashboards and SLA governance</li>
                </ul>
                <a href="#managed" className="text-gray-700 font-medium text-sm flex items-center gap-1 hover:gap-2 transition-all">
                  See Managed Services <ArrowRight className="w-4 h-4" />
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* AI Services Detail */}
      <section className="py-20 px-4 bg-gray-50" id="ai-detail">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <p className="text-red-600 font-semibold text-sm tracking-wider uppercase mb-2">AI Services, Deep Dive</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">AI for every stage of your business</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Whether you're a startup building from zero, an enterprise modernising legacy systems, or a SaaS company embedding AI into your product, we've done it before.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="pt-6">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <Settings className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">AI Strategy & Readiness</h3>
                <p className="text-gray-600 text-sm mb-4">Maturity assessments, use-case ranking by ROI, AI roadmaps, and governance frameworks, aligned to your business stage and goals.</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">Maturity Audit</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">Use-Case Ranking</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">AI Roadmap</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">Governance</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Database className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">AI Data & Infrastructure</h3>
                <p className="text-gray-600 text-sm mb-4">Clean data pipelines, feature engineering, MLOps platforms, and vector-data foundations that make your data AI-ready at scale.</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">Data Pipelines</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">Feature Engineering</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">MLOps</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">Vector DBs</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="pt-6">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <Brain className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">AI Models & Applications</h3>
                <p className="text-gray-600 text-sm mb-4">Generative AI workbenches for startups, RAG-based assistants for enterprises, and predictive models embedded into your SaaS product.</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">Generative AI</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">RAG Assistants</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">Predictive Models</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">Anomaly Detection</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-orange-500">
              <CardContent className="pt-6">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">AI-Powered Workflows</h3>
                <p className="text-gray-600 text-sm mb-4">AI-driven automation for ops, sales, support, engineering, and R&D, with approval gates, audit trails, and governance your compliance team requires.</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">Ops Automation</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">Sales AI</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">Support AI</span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">R&D Workflows</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Assessment CTA */}
          <div className="bg-gray-900 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-xl">
              <p className="text-red-500 text-xs font-bold tracking-wider uppercase mb-2">Free · 2 Minutes</p>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">Not sure where to start with AI?<br/>Take the free AI readiness assessment.</h3>
              <p className="text-gray-400 text-sm">Answer 5 questions and get a personalised AI maturity score, a readiness level, and a recommended next step, in under 2 minutes.</p>
            </div>
            <Button 
              size="lg" 
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-6 whitespace-nowrap"
              onClick={() => setShowAssessment(true)}
            >
              <CheckCircle className="mr-2 w-5 h-5" />
              Take the Free AI Readiness Assessment
            </Button>
          </div>
        </div>
      </section>

      {/* Agentic AI Pods Section */}
      <section className="py-20 px-4 bg-gray-900" id="pods">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-12">
            <p className="text-red-500 font-semibold text-sm tracking-wider uppercase mb-2">Agentic AI Pods</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">We build AI agents for your business.<br/>You own the <span className="text-red-500">governed outcome.</span></h2>
            <p className="text-lg text-gray-400 max-w-2xl mb-6">Move beyond passive copilots. Deploy fully managed, autonomous AI agents that execute complex workflows, governed by your rules, and operated by our experts.</p>
            <div className="flex flex-wrap gap-4">
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-red-600 hover:bg-red-700 text-white">Design Your First Agentic Pod</Button>
              </a>
            </div>
          </div>

          {/* Pillars */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <div className="w-10 h-10 bg-red-600/20 rounded-lg flex items-center justify-center mb-4">
                <Layers className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="font-semibold text-white mb-2">Purpose-Built for the Job</h3>
              <p className="text-sm text-gray-400">Every Agentic Pod is designed to understand your specific business goals, the tools the agents can use, and the boundaries of what they cannot touch.</p>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <div className="w-10 h-10 bg-red-600/20 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="font-semibold text-white mb-2">Governed by Default</h3>
              <p className="text-sm text-gray-400">Nothing reaches your customers or critical systems without your approval. Every output lands in your secure queue, ensuring total control and audit trail.</p>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <div className="w-10 h-10 bg-red-600/20 rounded-lg flex items-center justify-center mb-4">
                <Activity className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="font-semibold text-white mb-2">Operated Delivery</h3>
              <p className="text-sm text-gray-400">You focus on results; we focus on infrastructure. We build, host, monitor, and refine the agents, handling edge cases and model drift.</p>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <div className="w-10 h-10 bg-red-600/20 rounded-lg flex items-center justify-center mb-4">
                <Monitor className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="font-semibold text-white mb-2">Systems of Execution</h3>
              <p className="text-sm text-gray-400">Intelligent orchestration across your workflows with minimal human input, transforming operations into true systems of execution.</p>
            </div>
          </div>

          {/* Pod Configurations */}
          <div>
            <p className="text-red-500 font-semibold text-xs tracking-wider uppercase mb-6">Pod Configurations</p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-white">Data & ML Pod</h3>
                  <span className="text-xs bg-red-600 text-white px-2 py-1 rounded">Most Common</span>
                </div>
                <p className="text-sm text-gray-400 mb-4">Builds your AI data foundation, trains, deploys, and monitors predictive or generative models end-to-end.</p>
                <div className="text-xs text-gray-500 border-t border-gray-700 pt-3">
                  <strong className="text-gray-300">Outcome example:</strong> Reduce customer churn by identifying at-risk accounts 30 days earlier.
                </div>
              </div>
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-white">Automation & Workflow Pod</h3>
                  <span className="text-xs bg-green-600 text-white px-2 py-1 rounded">Fast ROI</span>
                </div>
                <p className="text-sm text-gray-400 mb-4">Designs and ships AI-powered workflow agents that automate repetitive ops, sales, or support with human-in-the-loop controls.</p>
                <div className="text-xs text-gray-500 border-t border-gray-700 pt-3">
                  <strong className="text-gray-300">Outcome example:</strong> Automate 70% of first-pass support ticket resolution using an LLM triage agent.
                </div>
              </div>
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-white">GenAI Product Pod</h3>
                  <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded">Strategic</span>
                </div>
                <p className="text-sm text-gray-400 mb-4">Embeds a GenAI capability into your core product — RAG-based assistants, fine-tuned models, multi-modal interfaces.</p>
                <div className="text-xs text-gray-500 border-t border-gray-700 pt-3">
                  <strong className="text-gray-300">Outcome example:</strong> Ship a context-aware AI assistant inside your SaaS product in 8 weeks.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Managed Services Section */}
      <section className="py-20 px-4 bg-gray-50" id="managed">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <p className="text-red-600 font-semibold text-sm tracking-wider uppercase mb-2">Managed Services</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">AI-enabled monitoring and reporting.<br/>Always on, always accurate.</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">We keep your AI systems healthy and your leadership informed, with AI-powered monitoring, automated reporting, and proactive anomaly detection running 24/7.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Features */}
            <div className="space-y-4">
              <div className="flex gap-4 p-4 bg-white rounded-xl border border-gray-200">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Activity className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">AI-Powered Infrastructure Monitoring</h3>
                  <p className="text-sm text-gray-600">Real-time monitoring of your AI systems, data pipelines, and cloud infrastructure using AI agents that detect issues before they become incidents.</p>
                </div>
              </div>
              <div className="flex gap-4 p-4 bg-white rounded-xl border border-gray-200">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Automated AI Reporting</h3>
                  <p className="text-sm text-gray-600">AI-generated performance reports, business intelligence summaries, and executive dashboards — delivered on your schedule, without manual effort.</p>
                </div>
              </div>
              <div className="flex gap-4 p-4 bg-white rounded-xl border border-gray-200">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Anomaly Detection & Alerting</h3>
                  <p className="text-sm text-gray-600">AI continuously analyses your data streams and model outputs, flagging deviations, data quality issues, and business metric anomalies in real time.</p>
                </div>
              </div>
              <div className="flex gap-4 p-4 bg-white rounded-xl border border-gray-200">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Layers className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Model Drift & Retraining Ops</h3>
                  <p className="text-sm text-gray-600">We monitor your AI models for performance drift and manage automated retraining pipelines so your models stay accurate without your team lifting a finger.</p>
                </div>
              </div>
            </div>

            {/* Metrics Panel */}
            <div className="bg-gray-900 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-2">What "managed" really means</h3>
              <p className="text-gray-400 text-sm mb-8">You get a dedicated team of AI operations engineers who treat your systems like their own. No alert fatigue. No reporting gaps. No model decay going unnoticed.</p>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-3xl font-bold text-white">24<span className="text-red-500">/7</span></div>
                  <div className="text-sm text-gray-500">AI-powered monitoring coverage</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">&lt;15<span className="text-red-500">min</span></div>
                  <div className="text-sm text-gray-500">Mean time to anomaly alert</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">99<span className="text-red-500">.9%</span></div>
                  <div className="text-sm text-gray-500">Uptime SLA for managed AI systems</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">0</div>
                  <div className="text-sm text-gray-500">Internal AI ops headcount needed</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-red-600">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to adopt AI and grow your revenue?</h2>
          <p className="text-lg text-white/80 mb-8">Tell us what you want AI to do. We respond within one business day.</p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Talk to an AI Architect <ArrowRight className="ml-2" size={20} />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default AIServices;
